import React from "react";
import useSWR from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { Factory, TrendingUp, AlertTriangle, CheckCircle2, Timer, Boxes, ClipboardList, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";

const fetcher = (u) => api.get(u).then(r => r.data);

const Kpi = ({ label, value, sub, tone="", testid, icon:Icon }) => (
  <div className={`kpi-card ${tone}`} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="kpi-label">{label}</div>
        <div className="kpi-value mt-1">{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
      </div>
      {Icon && <Icon size={18} className="text-slate-400"/>}
    </div>
  </div>
);

const STATUS_COLORS = { RUNNING:"#10B981", IDLE:"#6366F1", BREAKDOWN:"#EF4444", SETUP:"#F59E0B", MAINTENANCE:"#8B5CF6" };

export default function Dashboard() {
  const { data: sum } = useSWR("/dashboard/summary", fetcher, { refreshInterval: 15000 });
  const { data: pos } = useSWR("/production-orders", fetcher);
  const { data: entries } = useSWR("/production-entries", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const { data: shortages } = useSWR("/mrp/shortages?limit=5", fetcher);
  const pMap = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));

  const s = sum || {};
  const machineStatus = s.machine_status || {};
  const pieData = Object.entries(machineStatus).map(([k,v])=>({ name:k, value:v }));
  const productionByShift = React.useMemo(() => {
    const g = { A:0, B:0, C:0 };
    (entries||[]).forEach(e => { g[e.shift] = (g[e.shift]||0) + (e.total||0); });
    return Object.entries(g).map(([k,v])=>({ shift: `Shift ${k}`, qty: v }));
  }, [entries]);
  const productionTrend = React.useMemo(() => {
    const days = {};
    (entries||[]).forEach(e => { const d = (e.timestamp||"").slice(0,10); days[d] = (days[d]||0) + (e.total||0); });
    return Object.entries(days).sort().slice(-7).map(([d,q])=>({ day: d.slice(5), qty: q }));
  }, [entries]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CEO Control Center</h1>
          <p className="text-sm text-slate-500">Live snapshot of Sangam Plastic Industries — updated every 15s</p>
        </div>
        <span className="pill pill-running" data-testid="live-indicator">● LIVE</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-today-production" label="Today's Production" value={(s.today_production||0).toLocaleString()} sub="pieces" icon={Factory} tone="status-good"/>
        <Kpi testid="kpi-month-production" label="Month Production" value={(s.month_production||0).toLocaleString()} sub="pieces" icon={TrendingUp}/>
        <Kpi testid="kpi-oee" label="OEE" value={`${s.oee||0}%`} sub="Avail × Perf × Quality" icon={CheckCircle2} tone={(s.oee||0)>75?"status-good":(s.oee||0)>60?"status-warn":"status-danger"}/>
        <Kpi testid="kpi-rejection" label="Rejection %" value={`${s.rejection_pct||0}%`} sub="Cumulative" icon={AlertTriangle} tone={(s.rejection_pct||0)<2?"status-good":(s.rejection_pct||0)<5?"status-warn":"status-danger"}/>
        <Kpi testid="kpi-pending-po" label="Pending Production Orders" value={s.pending_production_orders||0} icon={ClipboardList}/>
        <Kpi testid="kpi-pending-qc" label="Pending QC" value={s.pending_qc||0} icon={ShieldAlert} tone={(s.pending_qc||0)>0?"status-warn":""}/>
        <Kpi testid="kpi-downtime" label="Downtime Today" value={`${s.downtime_today_min||0} min`} icon={Timer} tone={(s.downtime_today_min||0)>60?"status-warn":""}/>
        <Kpi testid="kpi-fg-stock" label="FG Available Stock" value={(s.fg_stock_qty||0).toLocaleString()} sub="units" icon={Boxes}/>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Production Trend (last 7 datapoints)</h3>
            <span className="text-[11px] text-slate-500">Total pcs / day</span>
          </div>
          <div style={{width:"100%", height:220}}>
            <ResponsiveContainer>
              <LineChart data={productionTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="day" tick={{fontSize:11, fill:"#64748B"}}/>
                <YAxis tick={{fontSize:11, fill:"#64748B"}}/>
                <Tooltip/>
                <Line type="monotone" dataKey="qty" stroke="#0284C7" strokeWidth={2} dot={{r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Machine Status</h3>
          <div style={{width:"100%", height:220}}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name">
                  {pieData.map((e) => <Cell key={e.name} fill={STATUS_COLORS[e.name] || "#94A3B8"}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {pieData.map(p => <span key={p.name} className={`pill ${STATUS_TO_PILL(p.name)}`}>{p.name}: {p.value}</span>)}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Production by Shift</h3>
          <div style={{width:"100%", height:200}}>
            <ResponsiveContainer>
              <BarChart data={productionByShift}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="shift" tick={{fontSize:11, fill:"#64748B"}}/>
                <YAxis tick={{fontSize:11, fill:"#64748B"}}/>
                <Tooltip/>
                <Bar dataKey="qty" fill="#0284C7" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Live Production Orders</h3>
          <div className="overflow-auto max-h-64">
            <table className="data-table" data-testid="dashboard-po-table">
              <thead><tr><th>PO No</th><th>Product</th><th>Progress</th><th>Status</th></tr></thead>
              <tbody>
              {(pos||[]).slice(0,8).map(p => (
                  <tr key={p.po_id}>
                    <td className="font-mono text-xs">{p.po_no}</td>
                    <td>{pMap[p.product_id]?.name || p.product_id}</td>
                    <td>
                      <div className="progress-bar w-32"><span style={{width: `${Math.min(100, ((p.actual_qty||0)/(p.planned_qty||1))*100)}%`}}/></div>
                      <span className="text-[11px] text-slate-500">{p.actual_qty||0} / {p.planned_qty||0}</span>
                    </td>
                    <td><span className={`pill ${STATUS_TO_PILL(p.status)}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="rm-shortages-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Top RM Shortages</h3>
            <Link to="/rm-planning" className="text-[11px] text-sky-600 hover:underline" data-testid="link-rm-planning">Open MRP →</Link>
          </div>
          {!shortages && <div className="text-xs text-slate-400">Loading…</div>}
          {shortages && shortages.rows.length===0 && (
            <div className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={14}/> No raw material shortages right now.</div>
          )}
          <div className="space-y-2">
            {(shortages?.rows||[]).map(r => (
              <div key={r.material_id || r.material_type} className="flex items-center justify-between border-b border-slate-100 pb-2" data-testid={`rm-shortage-${r.material_id || r.material_type}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{r.name}</div>
                  <div className="text-[10px] text-slate-500">{r.material_type}{r.grade ? ` · ${r.grade}` : ""} · stock {r.stock_kg} kg</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono font-semibold text-red-700">−{r.net_shortage_kg} kg</div>
                  {r.suggested_order_kg>0 && <div className="text-[10px] text-slate-500">order {r.suggested_order_kg} kg</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
